import React from 'react';
import { Box, Grid, Stack, Typography, Chip } from '@mui/material';
import VerifiedUserOutlined from '@mui/icons-material/VerifiedUserOutlined';
import MovieOutlined from '@mui/icons-material/MovieOutlined';
import PublicIcon from '@mui/icons-material/Public';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { IAudition } from '../../types/global/helper';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { TextWithIcon } from '../TextWithIcon/TextWithIcon';
import { formatWithOrdinal, getPayRangeText, splitPayRangeText } from '../../utils/AppUtils';
import { AUDITION_FIELD_ACCENT } from './auditionFieldTheme';

const META_SURFACE = (isDark: boolean) =>
    isDark
        ? {
              bgcolor: '#1a1a1a',
              border: '1px solid rgba(255,255,255,0.06)',
          }
        : {
              bgcolor: theme.light.secondaryBg,
              border: `1px solid ${theme.light.darkGray60}`,
          };

interface AuditionMetaBentoProps {
    audition: IAudition;
}

/**
 * Bento-style meta grid (dates, pay, union, type of work) + usage row.
 * Replaces stacked AuditionDetails / Union / type-of-work cards for the casting detail layout.
 */
export const AuditionMetaBento: React.FC<AuditionMetaBentoProps> = ({ audition }) => {
    const { themeStyle } = useThemeContext();
    const isDark = themeStyle === 'dark';
    const surface = META_SURFACE(isDark);
    const roles = audition.roles?.filter(role => role != null) || [];
    const payRangeText = audition.compensation_range_text ?? getPayRangeText(roles);
    const payIntroAmounts = payRangeText.trim() ? splitPayRangeText(payRangeText) : { intro: '', amounts: '' };
    const freqSuffix =
        audition.reward_frequency && audition.reward_frequency !== 'none'
            ? ` / ${audition.reward_frequency}`
            : '';
    const shootLabel = audition.shoot_location?.address || 'Remote';
    const talentLine = audition.talent_location?.address?.trim();
    const locationSecondary = talentLine ? `${shootLabel} / ${talentLine}` : shootLabel;

    const labelSx = {
        fontSize: 10,
        fontFamily: FONT_ENUM.MAIN_BOLD,
        color: AUDITION_FIELD_ACCENT,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.2em',
        display: 'block',
        mb: 1,
    };

    const bentoCardSx = {
        borderRadius: 2,
        p: { xs: 2, sm: 3 },
        height: '100%',
        transition: 'background-color 0.2s ease',
        '&:hover': isDark ? { bgcolor: '#20201f' } : {},
        ...surface,
    };

    const cell = (children: React.ReactNode) => (
        <Box sx={bentoCardSx} display="flex" flexDirection="column" justifyContent="space-between">
            {children}
        </Box>
    );

    const iconBoxSx = {
        bgcolor: isDark ? '#000' : theme[themeStyle].form_gray,
        p: 1.5,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };

    return (
        <Stack spacing={3}>
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    {cell(
                        <>
                            <Typography component="span" sx={labelSx}>
                                Dates &amp; Location
                            </Typography>
                            <Box>
                                <Typography
                                    sx={{
                                        fontSize: { xs: 18, sm: 20 },
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: theme[themeStyle].tag_white,
                                        fontWeight: 600,
                                    }}>
                                    Starts {formatWithOrdinal(audition.job_start_date, false)}
                                </Typography>
                                <Typography
                                    sx={{
                                        mt: 0.5,
                                        fontSize: 14,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        color: theme[themeStyle].light_gray_text,
                                    }}>
                                    {locationSecondary}
                                </Typography>
                            </Box>
                        </>,
                    )}
                </Grid>
                <Grid item xs={12} md={6}>
                    {cell(
                        <>
                            <Typography component="span" sx={labelSx}>
                                Compensation
                            </Typography>
                            <Box>
                                {!payRangeText.trim() ? (
                                    <Typography
                                        sx={{
                                            fontSize: { xs: 18, sm: 20 },
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: theme[themeStyle].tag_white,
                                            fontWeight: 600,
                                        }}>
                                        TBD
                                    </Typography>
                                ) : payIntroAmounts.intro ? (
                                    <>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 18, sm: 20 },
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                color: theme[themeStyle].tag_white,
                                                fontWeight: 600,
                                            }}>
                                            {payIntroAmounts.intro}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                mt: 0.5,
                                                fontSize: 14,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                color: theme[themeStyle].light_gray_text,
                                            }}>
                                            {payIntroAmounts.amounts}
                                            {freqSuffix}
                                        </Typography>
                                    </>
                                ) : (
                                    <Typography
                                        sx={{
                                            fontSize: { xs: 18, sm: 20 },
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: theme[themeStyle].tag_white,
                                            fontWeight: 600,
                                        }}>
                                        {payIntroAmounts.amounts}
                                        {freqSuffix}
                                    </Typography>
                                )}
                            </Box>
                        </>,
                    )}
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box
                        sx={{
                            ...bentoCardSx,
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 2,
                            display: 'flex',
                        }}>
                        <Box sx={iconBoxSx}>
                            <VerifiedUserOutlined sx={{ fontSize: 22, color: AUDITION_FIELD_ACCENT }} />
                        </Box>
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: theme[themeStyle].light_gray_text,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.12em',
                                    display: 'block',
                                    mb: 0.5,
                                }}>
                                Union Affiliation
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 15,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].tag_white,
                                    fontWeight: 500,
                                    textTransform: 'capitalize',
                                }}>
                                {audition.union_affiliation || 'Not Applicable'}
                            </Typography>
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box
                        sx={{
                            ...bentoCardSx,
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 2,
                            display: 'flex',
                        }}>
                        <Box sx={iconBoxSx}>
                            <MovieOutlined sx={{ fontSize: 22, color: AUDITION_FIELD_ACCENT }} />
                        </Box>
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: theme[themeStyle].light_gray_text,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.12em',
                                    display: 'block',
                                    mb: 0.5,
                                }}>
                                Type of Work
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 15,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: theme[themeStyle].tag_white,
                                    fontWeight: 500,
                                    textTransform: 'capitalize',
                                }}>
                                {audition.type_of_work || '—'}
                            </Typography>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            {audition.usage && audition.usage.length > 0 && (
                <Box>
                    <TextWithIcon
                        icon={<PublicIcon sx={{ fontSize: 18, color: theme[themeStyle].tag_white }} />}
                        text="Usage"
                    />
                    <Box sx={{ ...bentoCardSx, mt: 2 }}>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {audition.usage.map((usageItem, index) => (
                                <Chip
                                    key={index}
                                    label={usageItem}
                                    size="small"
                                    sx={{
                                        backgroundColor: '#000',
                                        color: theme[themeStyle].true_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        borderRadius: '6px',
                                        fontSize: 10,
                                        height: '24px',
                                        textTransform: 'capitalize',
                                    }}
                                />
                            ))}
                        </Stack>
                    </Box>
                </Box>
            )}
        </Stack>
    );
};
